/**
 * useMarketMonitorLive
 *
 * Page-level hook that owns the singleton `MarketMonitorLiveClient` for the
 * Market Monitor page. Wraps:
 *   - feature-flag gate via `REACT_APP_MARKET_MONITOR_LIVE`
 *   - cadence persistence in localStorage
 *   - lifecycle (open on mount, close on unmount)
 *   - status / result / error / stale fan-out via React state
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";

import {
  DEFAULT_TICK_CADENCE,
  MarketMonitorLiveClient,
  type ConnectionStatus,
  type ServerErrorPayload,
  type TailSample,
  type TickCadence,
} from "./marketMonitorLiveClient";
import type { DslResultPayload } from "../../../services/DslService";

const CADENCE_STORAGE_KEY = "mm.cadence";

const isLiveEnabled = (): boolean =>
  process.env.REACT_APP_MARKET_MONITOR_LIVE === "true";

const validCadences: TickCadence[] = [
  "seconds_1",
  "seconds_5",
  "seconds_10",
  "seconds_30",
  "minutes_1",
  "minutes_5",
  "minutes_30",
  "hours_1",
];

const readPersistedCadence = (): TickCadence => {
  try {
    const raw = window.localStorage.getItem(CADENCE_STORAGE_KEY);
    if (raw && (validCadences as string[]).includes(raw)) {
      return raw as TickCadence;
    }
  } catch {
    // localStorage may be unavailable (e.g. SSR); fall back to default.
  }
  return DEFAULT_TICK_CADENCE;
};

const writePersistedCadence = (value: TickCadence): void => {
  try {
    window.localStorage.setItem(CADENCE_STORAGE_KEY, value);
  } catch {
    // ignore
  }
};

export interface MarketMonitorLiveTagState {
  status: "loading" | "live" | "stale" | "error";
  result: DslResultPayload | null;
  seedVersion: number | null;
  error: ServerErrorPayload | null;
  staleSince: string | null;
}

export const applyAppended = (
  current: DslResultPayload | null,
  appended: TailSample[],
): DslResultPayload | null => {
  if (!current || current.type !== "time_series") return current;
  const dates = current.dates.slice();
  const values = current.values.slice();
  for (const sample of appended) {
    if (dates.length > 0 && dates[dates.length - 1] === sample.date) {
      values[values.length - 1] = sample.value;
    } else {
      dates.push(sample.date);
      values.push(sample.value);
    }
  }
  return { type: "time_series", dates, values };
};

export interface MarketMonitorLiveContext {
  enabled: boolean;
  client: MarketMonitorLiveClient | null;
  status: ConnectionStatus;
  cadence: TickCadence;
  setCadence: (next: TickCadence) => void;
  tagStates: Record<string, MarketMonitorLiveTagState>;
  resetTagState: (tagId: string) => void;
}

export const useMarketMonitorLive = (): MarketMonitorLiveContext => {
  const enabled = useMemo(isLiveEnabled, []);
  const clientRef = useRef<MarketMonitorLiveClient | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [cadence, setCadenceState] = useState<TickCadence>(() =>
    enabled ? readPersistedCadence() : DEFAULT_TICK_CADENCE,
  );
  const [tagStates, setTagStates] = useState<
    Record<string, MarketMonitorLiveTagState>
  >({});

  // Lazy-create the client on the first render where enabled is true.
  useEffect(() => {
    if (!enabled) return undefined;
    const client = new MarketMonitorLiveClient();
    clientRef.current = client;

    client.onStatusChange((s) => setStatus(s));
    client.onSeed((tagId, seedVersion, result) => {
      setTagStates((prev) => ({
        ...prev,
        [tagId]: {
          status: "live",
          result,
          seedVersion,
          error: null,
          staleSince: null,
        },
      }));
    });
    client.onDelta((tagId, seedVersion, appended) => {
      setTagStates((prev) => {
        const existing = prev[tagId];
        if (
          !existing ||
          existing.seedVersion !== seedVersion ||
          !existing.result ||
          existing.result.type !== "time_series"
        ) {
          // Drift: ask the server to clear its cursor so the next cadence
          // re-sends a Seed. Drop the local state for this tag so the chart
          // shows a loading placeholder until the Seed lands.
          clientRef.current?.resubscribe(tagId);
          if (!(tagId in prev)) return prev;
          const next = { ...prev };
          delete next[tagId];
          return next;
        }
        const nextResult = applyAppended(existing.result, appended);
        return {
          ...prev,
          [tagId]: { ...existing, result: nextResult },
        };
      });
    });
    client.onScalar((tagId, result) => {
      setTagStates((prev) => {
        const existing = prev[tagId];
        return {
          ...prev,
          [tagId]: {
            status: "live",
            result,
            seedVersion: existing?.seedVersion ?? null,
            error: null,
            staleSince: null,
          },
        };
      });
    });
    client.onError((tagId, err) => {
      setTagStates((prev) => ({
        ...prev,
        [tagId]: {
          status: "error",
          result: prev[tagId]?.result ?? null,
          seedVersion: prev[tagId]?.seedVersion ?? null,
          error: err,
          staleSince: null,
        },
      }));
    });
    client.onStale((tagId, since) => {
      setTagStates((prev) => {
        const existing = prev[tagId];
        return {
          ...prev,
          [tagId]: {
            status: "stale",
            result: existing?.result ?? null,
            seedVersion: existing?.seedVersion ?? null,
            error: null,
            staleSince: since,
          },
        };
      });
    });

    client.open(cadence);

    return () => {
      client.close();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Push cadence changes through to the server + persist.
  useEffect(() => {
    if (!enabled) return;
    writePersistedCadence(cadence);
    clientRef.current?.setCadence(cadence);
  }, [enabled, cadence]);

  const setCadence = useCallback(
    (next: TickCadence) => setCadenceState(next),
    [],
  );

  const resetTagState = useCallback((tagId: string) => {
    setTagStates((prev) => {
      if (!(tagId in prev)) return prev;
      const next = { ...prev };
      delete next[tagId];
      return next;
    });
  }, []);

  return {
    enabled,
    client: clientRef.current,
    status,
    cadence,
    setCadence,
    tagStates,
    resetTagState,
  };
};
